'use client';

import { useEffect } from 'react';
import { getCurrentUser } from '@/app/apis';
import { isEnterpriseDistribution, NETWORK_SETTINGS, resolveApiPath } from '@/app/constants';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { hysdk, initSensors, sensorsSignOut, sensorsSignUp, setRegisterPage } from '@/app/utils';
import { cloneRequestWithUrl, processReferrer, safeStorage, setupChunkLoadErrorRecovery } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import { client, usersLogout, viduGetRegion } from './apis';
import { updateTrack } from './update-track';
import { useReportPage, useSentryUser } from './use-sentry';

client.setConfig(NETWORK_SETTINGS);

// 定制版构建下，把所有 SDK 请求 URL 注入 `/xcustom-platform/`：
//   /vidu/v1/templates → /vidu/xcustom-platform/v1/templates
// SDK 产物本身只生成公网形态，由此 interceptor 在运行时把请求改写到定制版后端的对应路径。
// `resolveApiPath` 自带白名单（仅 vidu/iam/tools/credit/infer），对 claw/craftify 等不改写；
// 已含 `/xcustom-platform/` 的路径幂等不改。
if (isEnterpriseDistribution) {
  client.interceptors.request.use(async (req) => {
    try {
      const url = new URL(req.url);
      const nextPath = resolveApiPath(url.pathname);
      if (nextPath === url.pathname) return req;
      url.pathname = nextPath;
      return await cloneRequestWithUrl(req, url.toString());
    } catch (error) {
      // 任一浏览器对 Request 构造的边界判定不一致都不应阻断请求；
      // 退回到原 request（URL 不改写），至少能让外层观察到后端 404 而不是静默"网络错误"。
      console.error('[xcustom-platform interceptor] failed to rewrite URL, falling back to original', error);
      return req;
    }
  });
}

export const GlobalClientInit = () => {
  const pathname = usePathname();
  const { userInfo, updateUserInfo, updateRegion, updateCountryLevel } = useStore((state) => ({
    userInfo: state.userInfo,
    updateUserInfo: state.updateUserInfo,
    updateRegion: state.updateRegion,
    updateCountryLevel: state.updateCountryLevel,
  }));

  const { run: getRegion } = useFetch(viduGetRegion, {
    manual: true,
    onSuccess: (data) => {
      updateRegion(data.data?.region ?? null);
      updateCountryLevel(data.data?.country_level ?? null);
    },
  });

  useEffect(() => {
    initSensors();
    processReferrer();
    const cleanupChunkLoadErrorRecovery = setupChunkLoadErrorRecovery();
    const utmTerm = new URLSearchParams(window.location.search).get('utm_term');
    if (utmTerm) {
      safeStorage.setItem('utm_term', utmTerm);
    }
    getRegion();
    updateTrack();

    return cleanupChunkLoadErrorRecovery;
  }, []);

  useSentryUser(userInfo);
  useReportPage(pathname);

  useEffect(() => {
    const initUserInfo = async (): Promise<void> => {
      if (hysdk.sniff.isViduApp && hysdk.sniff.os === 'android') {
        const isAppLogged = await hysdk.isLogin();
        if (isAppLogged) {
          const appUserInfo = await hysdk.getUserInfo();
          updateUserInfo(appUserInfo);
          sensorsSignUp(appUserInfo.id);
        } else {
          usersLogout({ body: {} });
          sensorsSignOut();
          updateUserInfo(null);
        }
        return;
      }

      try {
        const { data } = await getCurrentUser();
        if (data) {
          updateUserInfo(data);
          setRegisterPage({
            uid: data.id,
            is_member: data.subs_plan !== 'free',
            member_type: data.subs_plan,
          });
          sensorsSignUp(data.id);
          return;
        }
      } catch {
        sensorsSignOut();
        updateUserInfo(null);
        return;
      }

      if (hysdk.sniff.isViduApp && hysdk.sniff.os === 'ios') {
        const isAppLogged = await hysdk.isLogin();
        if (isAppLogged) {
          const appUserInfo = await hysdk.getUserInfo();
          updateUserInfo(appUserInfo);
          sensorsSignUp(appUserInfo.id);
        } else {
          sensorsSignOut();
          updateUserInfo(null);
        }
        return;
      }

      sensorsSignOut();
      updateUserInfo(null);
    };

    initUserInfo();
  }, [updateUserInfo]);

  return null;
};
